'use client'

import { useState, useRef, useCallback } from 'react'
import {
  Bookmark,
  Forward,
  Heart,
  MessageCircle,
  Music2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'

type Asset = {
  type: string
  url: string | null
  thumbnailUrl: string | null
  title: string | null
}

type TikTokPostCardProps = {
  imageUrl: string | null
  caption: string
  hashtags: string[]
  username: string
  images?: string[]
  assets?: Asset[]
}

function Carousel({
  assets,
  caption,
  images,
}: {
  assets: Asset[]
  caption: string
  images?: string[]
}) {
  const [slide, setSlide] = useState(0)
  const total = assets.length
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)

  const goNext = useCallback(() => {
    setSlide((s) => Math.min(s + 1, total - 1))
  }, [total])

  const goPrev = useCallback(() => {
    setSlide((s) => Math.max(s - 1, 0))
  }, [])

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true)
    setStartX(e.clientX)
    setDragOffset(0)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return
    setDragOffset(e.clientX - startX)
  }

  const handlePointerUp = () => {
    if (!isDragging) return
    setIsDragging(false)
    if (dragOffset < -50 && slide < total - 1) {
      goNext()
    } else if (dragOffset > 50 && slide > 0) {
      goPrev()
    }
    setDragOffset(0)
  }

  if (total === 1) {
    const asset = assets[0]
    const displayUrl = asset.type === 'VIDEO' ? asset.thumbnailUrl : asset.url
    return (
      <div className="relative max-w-120 w-full rounded-2xl overflow-hidden bg-black">
        <img
          src={displayUrl ?? undefined}
          alt={caption.slice(0, 80)}
          className="max-w-full max-h-full object-contain"
        />
        <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/10 to-transparent" />
      </div>
    )
  }

  const translateX = -(slide * 100) + dragOffset

  return (
    <div className="relative max-w-120 w-full rounded-2xl overflow-hidden bg-black">
      <div
        className="flex select-none"
        style={{ transform: `translateX(${translateX}%)` }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {assets.map((asset) => {
          const displayUrl =
            asset.type === 'VIDEO' ? asset.thumbnailUrl : asset.url
          return (
            <div
              key={asset.url ?? asset.title ?? ''}
              className="w-full shrink-0"
            >
              <img
                src={displayUrl ?? undefined}
                alt={caption.slice(0, 80)}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )
        })}
      </div>
      <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/10 to-transparent pointer-events-none" />

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            disabled={slide === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 size-7 rounded-full bg-black/40 text-white flex items-center justify-center disabled:opacity-0 hover:bg-black/60 transition-colors"
            aria-label="Previous asset"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={slide >= total - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 size-7 rounded-full bg-black/40 text-white flex items-center justify-center disabled:opacity-0 hover:bg-black/60 transition-colors"
            aria-label="Next asset"
          >
            <ChevronRight className="size-4" />
          </button>
        </>
      )}

      {total > 1 && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-1">
          {assets.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setSlide(i)}
              className={`size-1.5 rounded-full transition-all ${
                i === slide ? 'bg-white w-4' : 'bg-white/50'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function TikTokPostCard({
  imageUrl,
  caption,
  hashtags,
  username,
  images,
  assets,
}: TikTokPostCardProps) {
  const t = useTranslations('TikTokPostCard')

  const carouselAssets =
    assets && assets.length > 0
      ? assets.filter((a) => a.url || a.thumbnailUrl)
      : []

  const imageAssets =
    images && images.length > 0
      ? images.map((url) => ({
          type: 'IMAGE',
          url,
          thumbnailUrl: null,
          title: null,
        }))
      : []

  const displayAssets =
    imageAssets.length > 0
      ? imageAssets
      : carouselAssets.length > 0
        ? carouselAssets
        : [{ type: 'IMAGE', url: imageUrl, thumbnailUrl: null, title: null }]

  return (
    <div className="flex flex-col items-center py-4 gap-3">
      <div className="relative max-w-120 w-full">
        <Carousel assets={displayAssets} caption={caption} />

        <div className="absolute right-3 bottom-20 flex flex-col items-center gap-5 pointer-events-none">
          <div
            className="size-10 rounded-full ring-2 ring-white"
            style={{
              background: 'linear-gradient(135deg, #ff0050 0%, #00f2ea 100%)',
            }}
            aria-hidden
          />
          {[
            { icon: Heart, count: '12.4k' },
            { icon: MessageCircle, count: '348' },
            { icon: Bookmark, count: '2.1k' },
            { icon: Forward, count: t('shareLabel') },
          ].map(({ icon: Icon, count }) => (
            <div key={count} className="flex flex-col items-center gap-0.5">
              <button
                type="button"
                className="flex size-10 items-center justify-center rounded-full bg-black/20 text-white backdrop-blur-sm transition-colors hover:bg-black/40 pointer-events-auto"
              >
                <Icon className="size-5" />
              </button>
              <span className="text-[10px] font-medium text-white">
                {count}
              </span>
            </div>
          ))}
        </div>

        <div className="absolute inset-x-3 bottom-4 flex flex-col gap-1.5 pointer-events-none">
          <span className="text-sm font-bold text-white">@{username}</span>
          <p className="text-sm text-white/90 line-clamp-2 leading-snug">
            {caption}
          </p>
          {hashtags.length > 0 && (
            <p className="text-sm font-semibold text-white/80 line-clamp-1">
              {hashtags.slice(0, 5).join(' ')}
            </p>
          )}
          <div className="flex items-center gap-1.5 text-white/80">
            <Music2 className="size-3 shrink-0" />
            <span className="text-xs truncate">
              {t('originalSound')} — @{username}
            </span>
          </div>
        </div>
      </div>

      <div className="w-full max-w-90">
        <Button
          type="button"
          className="w-full"
          onClick={() => toast.info(t('comingSoon'))}
        >
          {t('publish')}
        </Button>
      </div>
    </div>
  )
}
